<script setup lang='ts'>
import { APP_ROUTER } from '@qctsw/common'
import { timeFrom } from '@qctsw/utils'
import type { OmitPageParam } from '~/composables/usePagination'
import { logicComplaintSearch } from '~/logic/complaint/search'
import type { ComplaintSearchParams, ComplaintSearchResponse } from '~/server/types'
import { useGetRouteQuery } from '~/shared/function'
import { getComplaintStatus } from '@/shared/function'

const props = withDefaults(defineProps<{
  filterParams: OmitPageParam<ComplaintSearchParams>
  filterRouteQuery: Record<string, any>
}>(), {

})
const router = useRouter()
const page = useGetRouteQuery('page', '1')
const { list, state, pagination, getPage } = logicComplaintSearch()

function formatIssue(
  data: ComplaintSearchResponse['issues'],
): {
    content: string
    tips: string
  }[] {
  if (!data)
    return []
  return data.map((v) => {
    return {
      content: v.issueLabel,
      tips: v.fissueLabel,
    }
  })
}
const scrollViewRef = ref<HTMLElement | null>(null)
useActivatedWatch(() => [
  router?.afterEach(async (to, from) => {
    if (to.path === from.path) {
      let isOnlyPageQueryChange = true
      for (const key in to.query) {
        if (key !== 'page' && to.query[key] !== from.query[key]) {
          isOnlyPageQueryChange = false
          break
        }
      }

      const toPage = Number.isNaN(Number(to.query.page)) ? 1 : Number(to.query.page)
      if (!isOnlyPageQueryChange || !to.query.page) {
        pagination.initPagination()
      }
      else if (scrollViewRef.value) {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: (scrollViewRef.value?.offsetTop || 0) - 100,
            behavior: 'instant',
          })
        })
      }
      getPage(toPage, props.filterParams)
    }
  }),
])

useActivated({
  activatedFn: () => {
    // 等待页面activatedFn执行后执行
    nextTick(() => {
      getPage(page.value, props.filterParams)
    })
  },
}, { mounted: false })

await getPage(page.value, props.filterParams)

function getMore() {
  const pageNum = pagination.pagination.page + 1
  getPage(pageNum, props.filterParams)
}
</script>

<template>
  <div ref="scrollViewRef" class="mt-sm bg-white rounded-md p-sm">
    <table class="block">
      <thead class="block">
        <tr class=" grid grid-cols-36 py-sm text-white text-center bg-blue-5 font-bold rounded-t-md">
          <td class="col-span-4">
            投诉编号
          </td>
          <td class="col-span-12">
            品牌-车系 标题
          </td>
          <td class="col-span-12">
            具体问题
          </td>
          <td class="col-span-4">
            投诉时间
          </td>
          <td class="col-span-4">
            投诉状态
          </td>
        </tr>
      </thead>
      <tbody class="block divide-y-1 divide-slate-2">
        <UtilLoadState tag-name="tr" :length="list.length" type="up" :state="state" :reload-handler="() => getPage(page, filterParams)">
          <template #loading>
            <tr v-for="index in 10" :key="index" class="h-17.5 w-full block flex items-center p-sm">
              <n-skeleton class="w-full h-full rounded-md" />
            </tr>
          </template>

          <baseList :state="state" @load="getMore">
            <template v-if="list.length">
              <tr v-for="v, i in list" :key="i" class="grid grid-cols-36 py-sm text-center gap-sm">
                <td class="col-span-4 text-3.5 flex items-center justify-center">
                  <NuxtLink :to="APP_ROUTER.complaintDetail(v.id)" class="hover:text-orange-6">
                    QT{{ v.id }}
                  </NuxtLink>
                  <span class="text-blue-5 ml-0.5">
                    <UtilQrcode :to="APP_ROUTER.complaintDetail(v.id)" />
                  </span>
                </td>
                <td class="col-span-12 overflow-hidden flex items-center justify-center ">
                  <NuxtLink
                    :to="APP_ROUTER.complaintDetail(v.id)"
                    class="truncate inline-block hover:text-orange-6"
                  >
                    {{ v.title }}
                  </NuxtLink>
                  <UtilHasMedia :is-img="v.existImg" :is-video="v.existVideo" :is-audio="v.existAudio" />
                </td>
                <td class="col-span-12 overflow-hidden">
                  <BasicLabel :data-list="formatIssue(v.issues)" />
                </td>
                <td class="col-span-4 text-3.25 grid place-content-center">
                  <time :datetime="v.createTime">{{ timeFrom(v.createTime) }}</time>
                </td>
                <td class="col-span-4 text-3.25 grid place-content-center" :class="[`status-${v.complainStatusCode}`]">
                  {{ getComplaintStatus(v.complainStatusCode) }}
                </td>
              </tr>
            </template>
            <tr v-else class="h-50 w-full grid place-content-center ">
              <td>
                <BasicEmpty class="m-auto" text="暂无相关数据" />
              </td>
            </tr>
          </baseList>
        </UtilLoadState>
        <!-- <tr v-if="pagination.pagination.pageCount" class="block">
          <BasicPagination
            tag-name="td"
            class="mb-xl mt-xl max-w-100 mx-auto"
            :page="pagination.pagination.page"
            :total="pagination.pagination.pageCount"
            :to="(page) => APP_ROUTER.complaintSearch({ page, ...filterRouteQuery })"
          />
        </tr> -->
      </tbody>
    </table>
  </div>
</template>

<style lang='scss' scoped>
@import url('@/assets/styles/status.scss');
</style>
